import { SkillDetailErrorBoundary } from "@domains/assistant/client/components/SkillDetailErrorBoundary.tsx";
import { initAssistantI18next } from "@domains/assistant/client/i18n/i18next.ts";
import { initConnectedAppsI18next } from "@domains/connected-apps/client/i18n/i18next.ts";
import { initRoutinesI18next } from "@domains/routines/client/i18n/i18next.ts";
import { AdjustmentsHorizontalIcon, BoltIcon, NewspaperIcon, PuzzlePieceIcon, SwatchIcon, UserCircleIcon, UserIcon } from "@heroicons/react/24/outline";
import type { AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import type { FireflyRuntime } from "@squide/firefly";
import { initI18next } from "./i18n/i18next.ts";

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const { getRequestHandlers } = await import("./api/mocks/getRequestHandlers.ts");

        runtime.registerRequestHandlers(getRequestHandlers());
    }
}

export const registerAccountSettings: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initAssistantI18next(runtime);
    initConnectedAppsI18next(runtime);
    initRoutinesI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "account-settings", "/account", UserIcon, i18nextInstance);

    const account = area.registerSidebarArea("account");

    const general = account.registerRoute("general", {
        path: "/general",
        landing: true,
        page: "AccountGeneral",
        lazy: async () => {
            const { AccountGeneralPage } = await import("./pages/AccountGeneralPage.tsx");

            return { element: <AccountGeneralPage /> };
        }
    });

    const appearance = account.registerRoute("appearance", {
        path: "/appearance",
        page: "AccountAppearance",
        lazy: async () => {
            const { AppearancePage } = await import("./pages/appearance/AppearancePage.tsx");

            return { element: <AppearancePage /> };
        }
    });

    const agent = area.registerSidebarArea("agent");

    const agentPreferences = agent.registerRoute("agent-preferences", {
        path: "/agent-preferences",
        page: "AccountAgentPreferences",
        lazy: async () => {
            const { AgentPreferencesPage } = await import("./pages/AgentPreferencesPage.tsx");

            return { element: <AgentPreferencesPage /> };
        }
    });

    const skills = agent.registerRoute("skills", {
        path: "/skills",
        page: "AccountAgentSkills",
        lazy: async () => {
            const { SkillsPage } = await import("./pages/skills/list/SkillsPage.tsx");

            return { element: <SkillsPage /> };
        }
    });

    const skillDetail = agent.registerTabsArea("skill-detail", "/skills/:skillSlug", {
        useRenderAsTitleOnly: () => true,
        commandMenu: false,
        errorElement: <SkillDetailErrorBoundary backHref="/account/skills" />
    });

    skillDetail.registerRoute("view", {
        index: true,
        page: "AccountAgentSkillDetail",
        lazy: async () => {
            const { SkillDetailPage } = await import("./pages/skills/detail/SkillDetailPage.tsx");

            return { element: <SkillDetailPage /> };
        }
    });

    const routines = agent.registerRoute("routines", {
        path: "/routines",
        page: "AccountAgentRoutines",
        lazy: async () => {
            const { RoutinesPage } = await import("./pages/routines/RoutinesPage.tsx");

            return { element: <RoutinesPage /> };
        }
    });

    const connectedApps = agent.registerRoute("connected-apps", {
        path: "/apps",
        page: "AccountConnectedApps",
        lazy: async () => {
            const { ConnectedAppsPage } = await import("./pages/connected-apps/ConnectedAppsPage.tsx");

            return { element: <ConnectedAppsPage /> };
        }
    });

    connectedApps.registerOverlay("detail", {
        segment: ":appSlug",
        page: "AccountConnectedAppDetail",
        lazy: async () => {
            const { ConnectedAppDetailRoute } = await import("./pages/connected-apps/ConnectedAppDetailRoute.tsx");

            return { element: <ConnectedAppDetailRoute /> };
        }
    });

    connectedApps.registerOverlay("custom-mcp-detail", {
        segment: "custom-mcp/:definitionId",
        page: "AccountCustomMcpDetail",
        lazy: async () => {
            const { CustomMcpDetailRoute } = await import("./pages/connected-apps/CustomMcpDetailRoute.tsx");

            return { element: <CustomMcpDetailRoute /> };
        }
    });

    general.registerLink({ priority: 300, icon: UserCircleIcon });
    appearance.registerLink({ priority: 290, icon: SwatchIcon });

    agentPreferences.registerLink({ priority: 200, icon: AdjustmentsHorizontalIcon });
    skills.registerLink({ priority: 195, icon: NewspaperIcon });
    connectedApps.registerLink({ priority: 190, icon: PuzzlePieceIcon });

    area.registerToUserMenu({ priority: 0 });

    return (deferredRuntime: FireflyRuntime) => {
        if (deferredRuntime.getFeatureFlag("weekly-brief", false)) {
            routines.registerLink({ priority: 193, icon: BoltIcon });
        }
    };
};
